import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Link,
  IconButton,
  Slide,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { trackPageView } from '../../utils/analytics';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CookieConsent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  // Let the footer / Cookie Policy page reopen preferences after the first choice.
  useEffect(() => {
    const openSettings = () => {
      try {
        const stored = localStorage.getItem('cookie-preferences');
        if (stored) setCookiePreferences({ ...JSON.parse(stored), necessary: true });
      } catch {}
      setShowSettings(true);
    };
    window.addEventListener('gt:open-cookie-settings', openSettings);
    return () => window.removeEventListener('gt:open-cookie-settings', openSettings);
  }, []);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    const preferences = localStorage.getItem('cookie-preferences');

    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (preferences) {
      const parsed = JSON.parse(preferences);
      setCookiePreferences(parsed);
      applyCookieSettings(parsed);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setCookiePreferences(allPreferences);
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-preferences', JSON.stringify(allPreferences));
    setShowBanner(false);
    applyCookieSettings(allPreferences);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setCookiePreferences(necessaryOnly);
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-preferences', JSON.stringify(necessaryOnly));
    setShowBanner(false);
    applyCookieSettings(necessaryOnly);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-preferences', JSON.stringify(cookiePreferences));
    setShowSettings(false);
    setShowBanner(false);
    applyCookieSettings(cookiePreferences);
  };

  const applyCookieSettings = (preferences) => {
    // Google Analytics
    if (preferences.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }

    // Marketing cookies
    if (preferences.marketing && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied'
      });
    }

    // Functional cookies
    if (preferences.functional) {
      // Enable functional cookies
      localStorage.setItem('functional-cookies-enabled', 'true');
    } else {
      localStorage.removeItem('functional-cookies-enabled');
    }

    if (preferences.analytics) {
      const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      trackPageView(path);
    }
  };

  const handlePreferenceChange = (type) => (event) => {
    if (type === 'necessary') return; // Necessary cookies cannot be disabled
    setCookiePreferences(prev => ({
      ...prev,
      [type]: event.target.checked
    }));
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Consent Banner */}
      {showBanner && (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          p: 2,
          borderRadius: 0,
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{
          maxWidth: 1200,
          mx: 'auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 2,
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="primary" />
              Cookie Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts.{' '}
              <Link href="/cookie-policy" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                Learn more
              </Link>
              {' '}about how we use cookies and your choices. By continuing to use our site, you agree to our{' '}
              <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            gap: 1,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowSettings(true)}
              startIcon={<SettingsIcon />}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              Manage Preferences
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAcceptNecessary}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              Accept Necessary Only
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleAcceptAll}
              sx={{ minWidth: isMobile ? '100%' : 'auto' }}
            >
              Accept All
            </Button>
          </Box>

          <IconButton
            onClick={() => setShowBanner(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Paper>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          Cookie Preferences
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            We use different types of cookies to improve your experience on our website.
            You can choose which categories of cookies you want to allow.
          </Typography>

          <Box sx={{ mt: 3 }}>
            {/* Necessary Cookies */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cookiePreferences.necessary}
                    disabled
                    sx={{ '&.Mui-disabled': { color: theme.palette.primary.main } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Necessary Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      These cookies are essential for the website to function properly and cannot be disabled.
                      They enable core functionality like security, network management, and accessibility.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Analytics Cookies */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cookiePreferences.analytics}
                    onChange={handlePreferenceChange('analytics')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Analytics Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      These cookies help us understand how visitors interact with our website by collecting
                      and reporting information anonymously. This helps us improve our website.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Marketing Cookies */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cookiePreferences.marketing}
                    onChange={handlePreferenceChange('marketing')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Marketing Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      These cookies are used to track visitors across websites to display ads that are relevant
                      and engaging for individual users.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Functional Cookies */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cookiePreferences.functional}
                    onChange={handlePreferenceChange('functional')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Functional Cookies</Typography>
                    <Typography variant="body2" color="text.secondary">
                      These cookies enable the website to provide enhanced functionality and personalization.
                      They may be set by us or by third-party providers whose services we have added to our pages.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            For more information about how we use cookies, please read our{' '}
            <Link href="/cookie-policy" target="_blank" rel="noopener noreferrer">
              Cookie Policy
            </Link>
            {' '}and{' '}
            <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)}>
            Cancel
          </Button>
          <Button onClick={handleSavePreferences} variant="contained">
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
